
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CardFooter } from '@/components/ui/card';

// Define the schema for email configuration validation
export const emailConfigSchema = z.object({
  id: z.string(),
  provider: z.enum(['smtp', 'sendgrid', 'mailgun', 'other']),
  host: z.string().optional().or(z.literal('')),
  port: z.coerce.number().int().optional(),
  username: z.string().optional().or(z.literal('')),
  password: z.string().optional().or(z.literal('')),
  encryption: z.enum(['none', 'tls', 'ssl']).optional(),
  apiKey: z.string().optional().or(z.literal('')),
  fromEmail: z.string().email('Invalid email address'),
  fromName: z.string().min(1, 'From name is required'),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  name: z.string().min(1, 'Configuration name is required'),
});

export type EmailConfigFormValues = z.infer<typeof emailConfigSchema>;

interface EmailConfigFormProps {
  config?: EmailConfigFormValues;
  onCancel: () => void;
  onSave: (values: EmailConfigFormValues) => Promise<void>;
  onTestConnection: (values: EmailConfigFormValues) => Promise<void>;
  isSaving: boolean;
  isTesting: boolean;
}

const EmailConfigForm: React.FC<EmailConfigFormProps> = ({
  config,
  onCancel,
  onSave,
  onTestConnection,
  isSaving,
  isTesting
}) => {
  const form = useForm<EmailConfigFormValues>({
    resolver: zodResolver(emailConfigSchema),
    defaultValues: config || {
      id: '',
      provider: 'smtp',
      host: '',
      port: 587,
      username: '',
      password: '',
      encryption: 'tls',
      apiKey: '',
      fromEmail: '',
      fromName: '',
      isDefault: false,
      isActive: true,
      name: '',
    },
  });
  
  const watchProvider = form.watch('provider');
  
  const handleSubmit = async (values: EmailConfigFormValues) => {
    await onSave(values);
  };
  
  const handleTestConnection = async () => {
    const result = await form.trigger();
    if (!result) return;
    
    await onTestConnection(form.getValues());
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Configuration Name</FormLabel>
              <FormControl>
                <Input placeholder="Main SMTP Server" {...field} />
              </FormControl>
              <FormDescription>
                A descriptive name for this email configuration
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="provider"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Provider</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select email provider" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="smtp">SMTP Server</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="mailgun">Mailgun</SelectItem>
                  <SelectItem value="other">Other Provider</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Choose your email service provider
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
  
        {watchProvider === 'smtp' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="host"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SMTP Host</FormLabel>
                    <FormControl>
                      <Input placeholder="smtp.gmail.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SMTP Port</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="587"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Common ports: 25, 465 (SSL), 587 (TLS)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
  
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input placeholder="your-email@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder="SMTP password or app password" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
  
            <FormField
              control={form.control}
              name="encryption"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Encryption</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select encryption type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="tls">TLS</SelectItem>
                      <SelectItem value="ssl">SSL</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The encryption method used by your SMTP server
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <FormField
            control={form.control}
            name="apiKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>API Key</FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    placeholder={`${watchProvider} API Key`} 
                    {...field} 
                  />
                </FormControl>
                <FormDescription>
                  The API key for your email service provider
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="fromEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="noreply@yourcompany.com" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription>
                  The email address that will appear as the sender
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="fromName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>From Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Your Company Name" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription>
                  The name that will appear as the sender
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
  
        <div className="flex items-center space-x-2 mt-2">
          <FormField
            control={form.control}
            name="isDefault"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Set as Default</FormLabel>
                  <FormDescription>
                    Use this configuration as the default for sending emails
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Enabled</FormLabel>
                  <FormDescription>
                    Whether this email configuration is active and can be used
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <CardFooter className="flex justify-between px-0 pt-4">
          <div className="flex space-x-2">
            <Button 
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            
            <Button 
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={isTesting}
            >
              {isTesting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing
                </>
              ) : (
                "Test Connection"
              )}
            </Button>
          </div>

          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              "Save Configuration"
            )}
          </Button>
        </CardFooter>
      </form>
    </Form>
  );
};

export default EmailConfigForm;
